import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, message, Modal, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import axios from 'axios'

export interface CollaboratorRow {
  coll_id: string
  orga_id: string
  prename: string
  lastname: string
  email: string
  phone_number: string
  rank_label?: string | null
  admin_access?: string | null
  parent_name?: string | null
}

interface OpenCollaboratorsProps {
  orgaId: string
}

function uniqueFilterOptions(values: (string | null | undefined)[]) {
  return Array.from(new Set(values.filter((v): v is string => !!v))).map(value => ({
    text: value,
    value,
  }))
}

export function OpenCollaborators({ orgaId }: OpenCollaboratorsProps) {
  const [collaborators, setCollaborators] = useState<CollaboratorRow[]>([])
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const loadCollaborators = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get<CollaboratorRow[]>(
        `/organisations/${orgaId}/collaborators/list`,
      )
      setCollaborators(data)
    } catch (error) {
      console.error(error)
      message.error('Impossible de charger les collaborateurs')
    } finally {
      setLoading(false)
    }
  }, [orgaId])

  useEffect(() => {
    loadCollaborators()
  }, [loadCollaborators])

  const handleDelete = useCallback(
    (coll: CollaboratorRow) => {
      Modal.confirm({
        title: 'Supprimer le collaborateur',
        content: `Êtes-vous certain de vouloir supprimer ${coll.prename} ${coll.lastname} ?`,
        okText: 'Supprimer',
        okType: 'danger',
        cancelText: 'Annuler',
        onOk: async () => {
          setDeletingId(coll.coll_id)
          try {
            await axios.delete(`/organisations/${orgaId}/collaborators/${coll.coll_id}`)
            message.success('Collaborateur supprimé')
            await loadCollaborators()
          } catch (error) {
            console.error(error)
            message.error('Erreur lors de la suppression')
          } finally {
            setDeletingId(null)
          }
        },
      })
    },
    [orgaId, loadCollaborators],
  )

  const columns: ColumnsType<CollaboratorRow> = useMemo(
    () => [
      {
        title: 'Prénom',
        dataIndex: 'prename',
        key: 'prename',
        sorter: (a, b) => a.prename.localeCompare(b.prename),
        filterSearch: true,
        filters: uniqueFilterOptions(collaborators.map(c => c.prename)),
        onFilter: (value, record) => record.prename === value,
      },
      {
        title: 'Nom',
        dataIndex: 'lastname',
        key: 'lastname',
        sorter: (a, b) => a.lastname.localeCompare(b.lastname),
        filterSearch: true,
        filters: uniqueFilterOptions(collaborators.map(c => c.lastname)),
        onFilter: (value, record) => record.lastname === value,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        filterSearch: true,
        filters: uniqueFilterOptions(collaborators.map(c => c.email)),
        onFilter: (value, record) => record.email === value,
      },
      {
        title: 'Téléphone',
        dataIndex: 'phone_number',
        key: 'phone_number',
      },
      {
        title: 'Rang hiérarchique',
        dataIndex: 'rank_label',
        key: 'rank_label',
        filters: uniqueFilterOptions(collaborators.map(c => c.rank_label)),
        onFilter: (value, record) => record.rank_label === value,
        render: (value?: string | null) => value || '-',
      },
      {
        title: 'Accès administrateur',
        dataIndex: 'admin_access',
        key: 'admin_access',
        filters: [
          { text: 'Administrateur', value: 'admin' },
          { text: 'Aucun', value: '' },
        ],
        onFilter: (value, record) => (record.admin_access || '') === value,
        render: (value?: string | null) =>
          value === 'admin' ? <Tag color="blue">Administrateur</Tag> : '-',
      },
      {
        title: 'CN : rang3',
        dataIndex: 'parent_name',
        key: 'parent_name',
        filters: uniqueFilterOptions(collaborators.map(c => c.parent_name)),
        onFilter: (value, record) => record.parent_name === value,
        render: (value?: string | null) => value || '-',
      },
      {
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        width: 160,
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              icon={<EditOutlined />}
              href={`/organisations/${orgaId}/collaborators/${record.coll_id}/edit`}
            >
              Modifier
            </Button>
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === record.coll_id}
              onClick={() => handleDelete(record)}
            >
              Supprimer
            </Button>
          </Space>
        ),
      },
    ],
    [collaborators, deletingId, handleDelete, orgaId],
  )

  return (
    <div style={{ marginTop: 16 }}>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        href={`/organisations/${orgaId}/collaborators/new`}
        style={{ marginBottom: 16 }}
      >
        Ajouter un collaborateur
      </Button>
      <Table
        rowKey="coll_id"
        loading={loading}
        columns={columns}
        dataSource={collaborators}
        scroll={{ x: true }}
        pagination={false}
      />
    </div>
  )
}
